/**
 * @fileoverview Run-only chart rift hook for in-chat charts.
 *
 * The chart sibling of {@link useRunRift}. A `createChart` chat result plots an
 * immutable run (no owning dataset), so this hook builds chart-aware SQL with
 * {@link chartRift} over `'{runId}.arrow'` and registers a plain
 * {@link RiftDescriptor} (`datasetId: null`) that renders through the shared
 * rift query layer — the same path board charts use, minus the dataset/board
 * coupling. Column types come inline from the `createChart` tool output rather
 * than from `datasets$`, so the chart is self-contained on thread reload.
 *
 * Re-execution remap is shared with {@link useRunRift} (see `run-rift.ts`): an
 * expired run re-run against the same datasource remaps the old runId to the
 * fresh one so historical chat charts rehydrate without rewriting the message.
 */

import { useValue } from "@legendapp/state/react";
import type { DataType } from "@repo/lanes";
import { chartRift } from "@repo/shared/rift";
import type { GraphConfig } from "@repo/shared/types/graph-config";

import {
  getVersionedRiftId,
  type RiftDescriptor,
  registerRiftDescriptor,
} from "../dataset-rifts";
import { resolveRunId, runRemapVersion$ } from "./run-rift";

/**
 * Reactively derives the run-only chart `riftId` for a `createChart` result and
 * registers its descriptor. `columnDefs` is the run's `name -> DataType` map
 * (from the tool output columns); `graphConfig` is the agent-authored chart
 * config. Both are static per chat message, so the descriptor is effectively a
 * single stable cache entry. Re-derives when the runId is remapped by a
 * re-execution. Returns `null` when there is no runId.
 */
export const useRunChartRift = (
  runId: string | null,
  graphConfig: GraphConfig,
  columnDefs: Record<string, DataType>,
): string | null => {
  const result = useValue<{
    riftId: string | null;
    descriptor: RiftDescriptor | null;
  }>(() => {
    // Subscribe to the remap version so a re-execution re-derives the id.
    runRemapVersion$.get();

    if (!runId) return { riftId: null, descriptor: null };

    const resolvedRunId = resolveRunId(runId);
    const sql = chartRift(
      `${resolvedRunId}.arrow`,
      null,
      null,
      columnDefs,
      graphConfig,
      null,
    );
    const riftId = getVersionedRiftId(sql, resolvedRunId);
    const descriptor: RiftDescriptor = {
      riftId,
      datasetId: null,
      runId: resolvedRunId,
      sql,
      riftConfig: null,
      boardFilters: null,
      columnsConfig: null,
      columnDefs,
    };
    return { riftId, descriptor };
  });

  if (result.descriptor) registerRiftDescriptor(result.descriptor);

  return result.riftId;
};
